<template>
    <div class="message-editor">
        <div class="message-editor-body" id="message-body">
            <editor-content :editor="editor"/>
        </div>
        <div class="message-editor-footer">
            <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
                <div class="menubar">
                    <button class="menubar__button" @click="openEmoji" type="button">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14.756" height="14.316"
                             viewBox="0 0 14.756 14.316">
                            <g transform="translate(0.44)">
                                <circle cx="7" cy="7" r="7" transform="translate(-0.44)" fill="#ffd93b"/>
                                <path
                                    d="M101.487,287.763a4.576,4.576,0,0,1-4.435-3.447.3.3,0,1,1,.578-.148,3.98,3.98,0,0,0,7.714,0,.3.3,0,0,1,.578.148A4.575,4.575,0,0,1,101.487,287.763Z"
                                    transform="translate(-94.329 -276.004)" fill="#3e4347"/>
                                <path
                                    d="M69.45,62.292a7.158,7.158,0,0,1-12.73,4.494A7.158,7.158,0,0,0,66.786,56.72,7.15,7.15,0,0,1,69.45,62.292Z"
                                    transform="translate(-55.134 -55.134)" fill="#f4c534"/>
                                <ellipse cx="1.097" cy="1.304" rx="1.097" ry="1.304" transform="translate(3.749 4.856)"
                                         fill="#3e4347"/>
                                <path
                                    d="M177.6,195.161a.236.236,0,0,1-.316-.075.2.2,0,1,1,.306-.251A.237.237,0,0,1,177.6,195.161Z"
                                    transform="translate(-172.258 -189.282)" fill="#5a5f63"/>
                                <ellipse cx="1.097" cy="1.304" rx="1.097" ry="1.304" transform="translate(8.374 4.856)"
                                         fill="#3e4347"/>
                                <path
                                    d="M342.959,195.161a.236.236,0,0,1-.316-.075.2.2,0,1,1,.306-.251A.237.237,0,0,1,342.959,195.161Z"
                                    transform="translate(-332.994 -189.282)" fill="#5a5f63"/>
                            </g>
                        </svg>
                    </button>
                </div>
            </editor-menu-bar>
            <div v-if="showEmoji" class="editor-emoji-picker test" v-click-outside="hideEmoji"
                 v-bind:style="{top: emojiY + 'px', left: emojiX + 'px' }">
                <VEmojiPicker @select="selectEmoji" :continuousList="true"/>
            </div>
        </div>
        <div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
            <template v-if="hasResults">
                <div
                    v-for="(user, index) in filteredUsers"
                    :key="user.id"
                    class="suggestion-list__item"
                    :class="{ 'is-selected': navigatedUserIndex === index }"
                    @click="selectUser(user)"
                >
                    <div class="image">
                        <img v-if="user.image" :src=user.image :alt=user.name>
                        <span v-else class="icon-camera"></span>
                    </div>
                    <div class="text">
                        {{ user.name }}
                        <span v-if="user.companyName" class="small"> <br>{{ user.companyName }}</span>
                    </div>
                </div>
            </template>
            <div v-else class="suggestion-list__item is-empty">
                Keine Nutzer gefunden
            </div>
        </div>
        <link-preview-small
            :preview-data="previewData"
            :preview-link="previewLink"
            :preview-stat="previewStat"
            :preview-show-slide="previewShowSlide"
            :preview-remove="removePreView">
        </link-preview-small>

        <textarea v-model="umContent" @change="changeContent" id="umContent" name="umContent"
                  style="display: none"></textarea>
        <textarea id="umLinkData" style="display: none" name="umLinkData"></textarea>
    </div>
</template>

<script>
import Fuse from 'fuse.js';
import axios from 'axios';
import tippy from 'tippy.js';
import ClickOutside from 'vue-click-outside';
import {Editor, EditorContent, EditorMenuBar} from 'tiptap';
import {Placeholder, Link} from 'tiptap-extensions';
import {VEmojiPicker} from 'v-emoji-picker';
import LinkPreviewSmall from '@/components/LinkPreviewSmall';
import CustomMention from './extensions/Mention';
import config from './config';

function decodeEntities (encodedString) {
    var textArea = document.createElement('textarea');
    textArea.innerHTML = encodedString;
    return textArea.value;
}

/* global $ */
export default {
    components: {
        LinkPreviewSmall,
        EditorMenuBar,
        EditorContent,
        VEmojiPicker
    },
    data () {
        return {
            // Create an `Editor` instance with some default content. The editor is
            // then passed to the `EditorContent` component as a `prop`
            showEmoji: false,
            emojiX: 0,
            emojiY: 0,
            emojiYPos: 'top',
            emojiPos: {top: 0 + 'px', left: 0 + 'px'},
            query: null,
            suggestionRange: null,
            json: '',
            html: '',
            waitClosing: false,
            filteredUsers: [],
            navigatedUserIndex: 0,
            previewData: null,
            previewStat: '',
            previewLink: '',
            umContent: '',
            previewShowSlide: false,
            insertMention: () => {
            },
            observer: null,
            editor: new Editor({
                content: '',
                extensions: [
                    // The editor will accept paragraphs and headline elements as part of its document schema.
                    new Placeholder({
                        emptyNodeText: 'Nachricht...',
                        emptyEditorClass: 'is-editor-empty',
                        emptyNodeClass: 'is-empty',
                        showOnlyWhenEditable: true,
                        showOnlyCurrent: true
                    }),
                    new Link({}),
                    new CustomMention({
                        matcher: {
                            allowSpaces: true
                        },
                        // a list of all suggested items
                        items: () => [],
                        // is called when a suggestion starts
                        onEnter: ({range, command}) => {
                            this.suggestionRange = range;
                            // we save the command for inserting a selected mention
                            // this allows us to call it inside of our custom popup
                            // via keyboard navigation and on click
                            this.insertMention = command;
                        },
                        // is called when a suggestion has changed
                        onChange: ({query, range, virtualNode}) => {
                            let _this = this;
                            if (query.length > 0) {
                                axios.post(config().api, {
                                    query: query,
                                    modus: 'searchUsersEditor'
                                })
                                    .then(function (response) {
                                        _this.query = query;
                                        _this.filteredUsers = response.data;
                                        _this.suggestionRange = range;
                                        _this.navigatedUserIndex = 0;
                                        _this.renderPopup(virtualNode);
                                    })
                                    .catch(function (error) {
                                        window.console.log(error);
                                    });

                            } else {
                                this.query = null;
                                this.filteredUsers = [];
                                this.suggestionRange = null;
                                this.navigatedUserIndex = 0;
                                this.destroyPopup();
                            }

                        },
                        // is called when a suggestion is cancelled
                        onExit: () => {
                            // reset all saved values
                            this.query = null;
                            this.filteredUsers = [];
                            this.suggestionRange = null;
                            this.navigatedUserIndex = 0;
                            this.destroyPopup();
                        },
                        // is called on every keyDown event while a suggestion is active
                        onKeyDown: ({event}) => {
                            if (event.keyCode === 38 || event.key === 'ArrowUp') {
                                this.upHandler();
                                return true;
                            }
                            // pressing down arrow
                            if (event.keyCode === 40 || event.key === 'ArrowDown') {
                                this.downHandler();
                                return true;
                            }
                            // pressing enter
                            if (event.keyCode === 13 || event.key === 'Enter') {
                                this.enterHandler();
                                return true;
                            }
                            return false;
                        },
                        // is called when a suggestion has changed
                        // this function is optional because there is basic filtering built-in
                        // you can overwrite it if you prefer your own filtering
                        // in this example we use fuse.js with support for fuzzy search
                        onFilter: (items, query) => {
                            if (!query) {
                                return items;
                            }
                            const fuse = new Fuse(items, {
                                tokenize: true,
                                threshold: 0.6,
                                keys: ['name']
                            });
                            return fuse.search(query);
                        }
                    })
                ],
                onUpdate: ({getHTML, getJSON}) => {
                    var links = decodeEntities(getHTML()).match(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-zA-Z]{2,}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g);
                    var _this = this;
                    // var html =  getHTML();
                    if (this.previewLink === '' && links && links.length > 0 && links[0] !== _this.previewLink) {
                        _this.previewStat = 'pending';
                        _this.previewLink = links[0];
                        axios.post(config().api, {
                            link: links[0],
                            modus: 'getLinkPreview'
                        })
                            .then(function (response) {
                                if (typeof response.data === 'object' && response.data.length !== 0) {
                                    _this.setLinkPreview(response.data);
                                    // html = _this.html.replace(/<a [^>]+>(.*?)<\/a>/, '')
                                    _this.editor.setContent(_this.html.replace(/<a [^>]+>(.*?)<\/a>/, ''));
                                    // _this.editor.setContent(_this.html.replace(/<a [^>]+>(.*?)<\/a>/, '<a href="' + links[0] + '" rel="noreferrer noopener nofollow">' + response.data.title + '</a>'));
                                    _this.json = getJSON();
                                    _this.umContent = _this.html.replace(/<a [^>]+>(.*?)<\/a>/, '');
                                    _this.html = _this.html.replace(/<a [^>]+>(.*?)<\/a>/, '');

                                } /*else {
                                      _this.removePreView();
                                  }*/
                            })
                            .catch(function (error) {
                                window.console.log(error);
                            });
                    } else {
                        this.json = getJSON();
                        this.umContent = this.html = getHTML();
                    }/*else if (!links || links.length === 0) {
                            _this.removePreView();
                        }*/

                }
            })
        };
    },
    methods: {
        selectEmoji (emoji) {
            const transaction = this.editor.state.tr.insertText(emoji.data);
            this.editor.view.dispatch(transaction);
        },
        openEmoji (event) {
            this.showEmoji = !this.showEmoji;
            // this.showEmoji = !this.showEmoji;
            this.emojiX = event.clientX;
            this.emojiY = event.clientY + 10;
            if (this.emojiX > ($(window).width() - 330)) {
                this.emojiX = $(window).width() - 330;
            }
            /*let left = event.clientX
            let top = event.clientY + 10
            if (this.emojiPos.left > ($(window).width() - 330)) {
                left = $(window).width() - 330;
            }
            if ($(window).width() < 786) {
                this.emojiPos  =  {
                    bottom: top,
                    left: left
                }
            } else {
                this.emojiPos  =  {
                    top: top,
                    left: left
                }
            }
            window.console.log(this.emojiPos)*/
            this.waitClosing = true;
            let _this = this;
            setTimeout(function () {
                _this.waitClosing = false;
            }, 200);
        },
        handleScroll () {
            this.showEmoji = false;
        },
        hideEmoji (event) {
            window.console.log($(event.target).closest('.message-editor-footer').length, this.showEmoji); // eslint-disable-line
            if (this.showEmoji === true && this.waitClosing === false) {
                this.showEmoji = false;
            }

        },
        // navigate to the previous item
        // if it's the first item, navigate to the last one
        upHandler () {
            this.navigatedUserIndex = ((this.navigatedUserIndex + this.filteredUsers.length) - 1) % this.filteredUsers.length;
        },
        // navigate to the next item
        // if it's the last item, navigate to the first one
        downHandler () {
            this.navigatedUserIndex = (this.navigatedUserIndex + 1) % this.filteredUsers.length;
        },
        enterHandler () {
            const user = this.filteredUsers[this.navigatedUserIndex];

            if (user) {
                this.selectUser(user);
            }
        },
        // we have to replace our suggestion text with a mention
        // so it's important to pass also the position of your suggestion text
        selectUser (user) {
            this.insertMention({
                range: this.suggestionRange,
                attrs: {
                    id: user.id,
                    label: user.name,
                    type: user.type
                }
            });
            this.editor.focus();
        },
        // renders a popup with suggestions
        // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
        renderPopup (node) {
            if (this.popup) {
                return;
            }

            this.popup = tippy(node, {
                content: this.$refs.suggestions,
                trigger: 'mouseenter',
                interactive: true,
                theme: 'dark',
                placement: 'top-start',
                inertia: true,
                duration: [400, 200],
                showOnInit: true,
                arrow: true,
                arrowType: 'round'
            });

            // we have to update tippy whenever the DOM is updated
            if (MutationObserver) {
                this.observer = new MutationObserver(() => {
                    this.popup.popperInstance.scheduleUpdate();
                });
                this.observer.observe(this.$refs.suggestions, {
                    childList: true,
                    subtree: true,
                    characterData: true
                });
            }
        },
        destroyPopup () {
            if (this.popup) {
                this.popup.destroy();
                this.popup = null;
            }

            if (this.observer) {
                this.observer.disconnect();
            }
        },
        setLinkPreview (data) {
            this.previewData = data;
            this.previewStat = 'show';
            let img = '';
            this.previewShowSlide = false;
            if (data.cover) {
                img = data.cover;
            } else if (typeof data.images === 'object') {
                let keys = Object.keys(data.images);
                img = data.images[keys[0]];
                this.previewShowSlide = keys.length > 1;
            }
            this.previewImage = img;
            $('#umLinkData').val(JSON.stringify({
                ...this.previewData,
                link: this.previewLink
            }));
        },
        removePreView (e) {
            if (typeof e !== 'undefined') {
                e.preventDefault();
            }
            this.previewData = null;
            this.previewStat = '';
            this.previewLink = '';
            this.previewShowSlide = false;
            $('#umLinkData').val('');
            return false;
        },
        changeContent () {
            if ($('#umContent').val() === '') {
                this.html = '';
                this.editor.clearContent(true);
            }
        }
    },
    computed: {
        hasResults () {
            return this.filteredUsers.length;
        },
        showSuggestions () {
            return this.query || this.hasResults;
        }
    },

    beforeDestroy () {
        // Always destroy your editor instance when it's no longer needed
        this.editor.destroy();
    },
    created () {
        window.addEventListener('scroll', this.handleScroll);
    },
    destroyed () {
        window.removeEventListener('scroll', this.handleScroll);
    },
    mounted () {
        document.body.addEventListener('resetPreviewLink', function () {
            this.previewData = null;
            this.previewStat = '';
            this.previewLink = '';
            this.previewShowSlide = false;
            $('#umLinkData').val('');
            window.console.log('Reset Previe Linke');
        }.bind(this));
    },
    directives: {
        ClickOutside
    }
};
</script>

<style lang="scss"></style>
